import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mails, ListChecks, Activity, UserX, Plus, Trash2, Save, Eye, Loader2, ChevronRight } from "lucide-react";
import { fmtDate } from "@/components/common";
import { toast } from "sonner";

const VARS = ["customer_name", "employee_name", "employee_phone", "company_name", "pdf_link", "video_link", "website_link", "lead_id", "product_name"];

export default function EmailAutomation() {
  const [seqs, setSeqs] = useState([]);
  const [editing, setEditing] = useState(null);   // {status_slug, enabled, steps:[]}
  const [preview, setPreview] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [events, setEvents] = useState(null);
  const [unsub, setUnsub] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSeqs = () => api.get("/email/sequences").then((r) => setSeqs(r.data.items)).catch(() => {});
  useEffect(() => { loadSeqs(); }, []);

  const open = async (slug) => {
    const { data } = await api.get(`/email/sequences/${slug}`);
    setEditing({ status_slug: slug, enabled: data.enabled ?? false, steps: data.steps || [] });
    setPreview(null);
  };
  const addStep = () => setEditing((e) => e.steps.length >= 7 ? e : ({ ...e, steps: [...e.steps, { subject: "", body: "", delay_minutes: e.steps.length === 0 ? 0 : 24 * 60, active: true }] }));
  const setStep = (i, k, v) => setEditing((e) => ({ ...e, steps: e.steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const rmStep = (i) => setEditing((e) => ({ ...e, steps: e.steps.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/email/sequences/${editing.status_slug}`, { enabled: editing.enabled, steps: editing.steps });
      toast.success("Sequence saved"); await loadSeqs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const doPreview = async (s) => {
    try { const { data } = await api.post("/email/preview", { subject: s.subject, body: s.body }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><Mails className="h-7 w-7 text-primary" /> Email Automation</h1>
        <p className="text-muted-foreground text-sm mt-1">Status-based sequences · max 7 emails · one active sequence per lead · current-employee personalization.</p>
      </div>

      <Tabs defaultValue="sequences">
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="sequences" data-testid="email-tab-sequences"><ListChecks className="h-4 w-4 mr-1.5" />Sequences</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="email-tab-deliveries" onClick={() => !deliveries && api.get("/email/deliveries").then((r) => setDeliveries(r.data))}><Mails className="h-4 w-4 mr-1.5" />Deliveries</TabsTrigger>
          <TabsTrigger value="events" data-testid="email-tab-events" onClick={() => !events && api.get("/email/events").then((r) => setEvents(r.data))}><Activity className="h-4 w-4 mr-1.5" />Events</TabsTrigger>
          <TabsTrigger value="unsub" data-testid="email-tab-unsub" onClick={() => !unsub && api.get("/email/unsubscribed").then((r) => setUnsub(r.data))}><UserX className="h-4 w-4 mr-1.5" />Unsubscribed</TabsTrigger>
        </TabsList>

        <TabsContent value="sequences" className="mt-4">
          {!editing ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {seqs.map((s) => (
                <button key={s.status_slug} data-testid={`sequence-${s.status_slug}`} onClick={() => open(s.status_slug)}
                  className="glass rounded-xl p-4 flex items-center justify-between hover:border-slate-600 text-left">
                  <div>
                    <div className="font-semibold">{s.status_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.step_count}/7 steps</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${s.enabled ? "text-emerald-400 border-emerald-500/30" : "text-slate-500 border-slate-700"}`}>{s.enabled ? "Enabled" : "Off"}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="glass rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={() => setEditing(null)} className="text-sm text-muted-foreground hover:text-foreground">← All sequences</button>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Enabled</Label>
                  <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} data-testid="sequence-enabled-toggle" />
                </div>
              </div>
              <h2 className="font-heading text-xl font-bold capitalize">{editing.status_slug.replace(/-/g, " ")} sequence</h2>
              <p className="text-[11px] text-muted-foreground">Variables: {VARS.map((v) => `{{${v}}}`).join("  ")}</p>

              {editing.steps.map((s, i) => (
                <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-2" data-testid={`step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-primary">Email {i + 1}</span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">Wait <Input type="number" min="0" step="0.5" value={Math.round((Number(s.delay_minutes || 0) / 60) * 100) / 100} onChange={(e) => setStep(i, "delay_minutes", Math.round(Number(e.target.value) * 60))} data-testid={`step-delay-${i}`} className="w-20 h-7 bg-slate-900/60 border-slate-700" /> hours</div>
                      <Switch checked={s.active !== false} onCheckedChange={(v) => setStep(i, "active", v)} data-testid={`step-active-${i}`} />
                      <button onClick={() => rmStep(i)} data-testid={`step-remove-${i}`} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <Input value={s.subject} onChange={(e) => setStep(i, "subject", e.target.value)} placeholder="Subject" data-testid={`step-subject-${i}`} className="bg-slate-900/60 border-slate-700" />
                  <Textarea value={s.body} onChange={(e) => setStep(i, "body", e.target.value)} placeholder="Body (supports variables)" data-testid={`step-body-${i}`} rows={4} className="bg-slate-900/60 border-slate-700" />
                  <button onClick={() => doPreview(s)} data-testid={`step-preview-${i}`} className="text-xs text-primary flex items-center gap-1"><Eye className="h-3 w-3" /> Preview</button>
                </div>
              ))}

              {preview && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="preview-box">
                  <div className="text-xs text-muted-foreground">Preview (sample data)</div>
                  <div className="font-semibold mt-1">{preview.subject}</div>
                  <div className="text-sm whitespace-pre-wrap mt-1">{preview.body}</div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button variant="outline" disabled={editing.steps.length >= 7} onClick={addStep} data-testid="add-step-button" className="border-slate-700"><Plus className="h-4 w-4 mr-1" />Add Email ({editing.steps.length}/7)</Button>
                <Button onClick={save} disabled={busy} data-testid="save-sequence-button" className="bg-primary text-primary-foreground">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save Sequence</>}</Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <div className="glass rounded-xl p-4">
            {!deliveries ? <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /> :
              deliveries.items.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No emails sent yet.</p> :
                <div className="space-y-2">{deliveries.items.map((d) => (
                  <div key={d.id} className="flex items-center justify-between text-sm border-b border-slate-800/60 py-2" data-testid={`delivery-${d.id}`}>
                    <div><div className="font-medium">{d.subject}</div><div className="text-xs text-muted-foreground">{d.recipient} · {d.status_slug} · {fmtDate(d.created_at)}</div></div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${d.delivery_status === "SENT" ? "text-emerald-400 border-emerald-500/30" : d.delivery_status === "FAILED" ? "text-red-400 border-red-500/30" : "text-slate-400 border-slate-700"}`}>{d.delivery_status}</span>
                  </div>))}</div>}
          </div>
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <div className="glass rounded-xl p-4">
            {!events ? <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /> :
              events.items.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No email events yet.</p> :
                <div className="space-y-1.5">{events.items.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm py-1" data-testid={`event-${e.id}`}>
                    <span className="font-mono text-xs text-primary">{e.event}</span>
                    <span className="text-[11px] text-muted-foreground">{fmtDate(e.created_at)}</span>
                  </div>))}</div>}
          </div>
        </TabsContent>

        <TabsContent value="unsub" className="mt-4">
          <div className="glass rounded-xl p-4">
            {!unsub ? <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /> :
              unsub.items.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No unsubscribed contacts.</p> :
                <div className="space-y-2">{unsub.items.map((u) => (
                  <div key={u.id} className="flex items-center justify-between text-sm border-b border-slate-800/60 py-2" data-testid={`unsub-${u.id}`}>
                    <span>{u.name} · <span className="text-muted-foreground">{u.email}</span></span>
                    <span className="font-mono text-xs text-primary">{u.lead_code}</span>
                  </div>))}</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
